import { useEffect, useState } from 'react';
import { detectARCapableDevice } from '@/services/Utility';

/**
 * Device detection result type
 * Matches the return type of detectARCapableDevice()
 * 
 * IMPORTANT: These values are based on DEVICE CAPABILITIES (input method), NOT screen size/viewport
 * - isDesktop = device with mouse/trackpad input (NOT about CSS viewport width)
 * - A tablet in landscape mode is still a touch device (isDesktop = false)
 * - Use for AR/VR feature detection, NOT for responsive CSS styling
 */
export type DeviceDetectionResult = {
  isARCapable: boolean;
  hasWebXR: boolean;
  /**
   * Based on touch vs pointer input — NOT viewport width, and NOT navigator.xr presence
   * (xr exists on HTTPS desktop browsers without a headset).
   */
  isDesktop: boolean;
  isMobile: boolean;
  isVR: boolean;
  hasTouchSupport: boolean;
  canUseImmersiveAR: boolean;
};

/**
 * Custom hook for device detection
 * Detects if the current device is desktop, mobile, or VR/AR capable
 * Caches the result to avoid repeated calculations
 * Returns full device detection information
 * 
 * IMPORTANT: Detection is based on DEVICE CAPABILITIES (input method), NOT screen size/viewport
 * - Use for AR/VR feature detection, NOT for responsive CSS styling
 * - For CSS styling, use media queries like @media (max-width: 768px) instead
 * 
 * @returns DeviceDetectionResult object with all device capabilities
 * 
 * @example
 * const deviceInfo = useDeviceDetection();
 * const isDesktop = deviceInfo.isDesktop; // Based on input capability, NOT viewport size
 * const isARCapable = deviceInfo.isARCapable; // boolean
 */
export function useDeviceDetection(): DeviceDetectionResult {
  const [deviceInfo, setDeviceInfo] = useState<DeviceDetectionResult>(() => {
    // Initialize with default values to avoid hydration issues
    return {
      isARCapable: false,
      hasWebXR: false,
      isDesktop: true, // Default to desktop (safer fallback)
      isMobile: false,
      isVR: false,
      hasTouchSupport: false,
      canUseImmersiveAR: false
    };
  });

  useEffect(() => {
    // Calculate device detection once on mount
    const detected = detectARCapableDevice();
    setDeviceInfo(detected);
  }, []);

  return deviceInfo;
}
